import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getTradeOrders } from "../Redux/Action/dashboard/tradeOrderAction";
import Constants from "../Utils/Constants";
import { CommonButton } from "../Components/ui/CommonButton";
import { useAppTheme } from "../Hooks/useAppTheme";

export const TradesOrder = () => {
  const theme = useAppTheme();
  const dispatch = useDispatch();
  const userData = useSelector((state) => state?.Login?.userData);
  const { tradeOrders, loading, error, pagination, summary } = useSelector((state) => state?.TradeOrders);

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [formattedTradeOrders, setFormattedTradeOrders] = useState([]);
  const [endDateMin, setEndDateMin] = useState("");
  const [endDateMax, setEndDateMax] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [filter, setFilter] = useState("recent"); // recent, lowToHigh, highToLow, maxProfit, minProfit

  // Log component mount/unmount
  useEffect(() => {
    console.log('TradesOrder component mounted');
    return () => {
      console.log('TradesOrder component unmounted');
    };
  }, []);

  // Set default dates (last 14 days)
  useEffect(() => {
    const today = new Date();
    const last14Days = new Date(today);
    last14Days.setDate(today.getDate() - 14);
    
    // Set end date to tomorrow
    const endDateDefault = new Date();
    endDateDefault.setDate(endDateDefault.getDate() + 1);
    
    // Format dates as "DD-MMM-YYYY HH:mm:ss"
    const formatDate = (date) => {
      const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      const day = String(date.getDate()).padStart(2, '0');
      const month = months[date.getMonth()];
      const year = date.getFullYear();
      return `${day}-${month}-${year} 00:00:00`;
    };
    
    const startDateFormat = formatDate(last14Days);
    const endDateFormat = formatDate(endDateDefault);
    
    setStartDate(startDateFormat);
    setEndDate(endDateFormat);
    
    // Store formatted dates for input fields
    setEndDateMin(formatDateForInput(last14Days));
    
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    setEndDateMax(formatDateForInput(tomorrow));
  }, []);

  // Track if we've already fetched orders to prevent multiple calls
  const [hasFetched, setHasFetched] = useState(false);

  // Automatically fetch orders when component mounts and dates are set
  useEffect(() => {
    console.log('TradeOrders useEffect triggered', { startDate, endDate, userData, hasFetched });
    if (startDate && endDate && userData && !hasFetched) {
      console.log('Calling handleFetchOrders');
      handleFetchOrders(1);
      setHasFetched(true);
    }
  }, [startDate, endDate, userData, hasFetched]);

  // Format date as YYYY-MM-DD for input type="date"
  const formatDateForInput = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  
  // Calculate min and max dates (30 days before and after today)
  const getMinMaxDates = () => {
    const today = new Date();
    const minDate = new Date(today);
    const maxDate = new Date(today);
    minDate.setDate(today.getDate() - 30);
    maxDate.setDate(today.getDate() + 30);
    
    return {
      minDate: formatDateForInput(minDate),
      maxDate: formatDateForInput(maxDate)
    };
  };
  
  const { minDate, maxDate } = getMinMaxDates();

  // Format trade orders when they change
  useEffect(() => {
    if (tradeOrders && Array.isArray(tradeOrders)) {
      setFormattedTradeOrders(tradeOrders);
    }
  }, [tradeOrders]);

  const handleFetchOrders = (page = 1) => {
    console.log('handleFetchOrders called with page:', page);

    if (startDate && endDate && userData) {
      // Handle different user roles
      let accountNumbers = "";
      
      if (userData.role === 6) {
        
     
        if (userData.ibAccounts && Array.isArray(userData.ibAccounts)) {
        
          accountNumbers = userData.ibAccounts.map(account => account.accountNo).join("&");
          
        }
      } else if (userData.role === 7) {
       
        accountNumbers = userData.accountno;
      }
      
      if (accountNumbers) {
        const payload = {
          accountno: accountNumbers,
          sdate: startDate,
          edate: endDate,
          page: page,
          pageSize: pageSize,
          filter: filter
        };
        console.log("payload",payload)
    
        dispatch(getTradeOrders(payload));
        setCurrentPage(page);
      }
    }
  };

  // Convert date from API format to input format (YYYY-MM-DD)
  const convertToInputFormat = (dateString) => {
    if (!dateString) return "";
    // Extract date part from "DD-MMM-YYYY HH:mm:ss"
    const datePart = dateString.split(' ')[0]; // "DD-MMM-YYYY"
    const [day, month, year] = datePart.split('-');
    
    // Convert month name to month number
    const months = {"Jan": "01", "Feb": "02", "Mar": "03", "Apr": "04", "May": "05", "Jun": "06", 
                   "Jul": "07", "Aug": "08", "Sep": "09", "Oct": "10", "Nov": "11", "Dec": "12"};
    const monthNumber = months[month];
    
    return `${year}-${monthNumber}-${day}`;
  };
  
  // Convert date from input format to API format
  const convertToApiFormat = (dateString, isEndDate = false) => {
    if (!dateString) return "";
    const dateObj = new Date(dateString);
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const day = String(dateObj.getDate()).padStart(2, '0');
    const month = months[dateObj.getMonth()];
    const year = dateObj.getFullYear();
    const time = isEndDate ? "23:59:59" : "00:00:00";
    return `${day}-${month}-${year} ${time}`;
  };
  
  const formatDateToDDMMYYYY = (dateString) => {
    if (!dateString || dateString === "N/A") return "N/A";
    
    // Handle ISO date format (2025-10-28T17:04:26)
    if (dateString.includes('T') && dateString.split('T').length === 2) {
      const dateObj = new Date(dateString);
      if (!isNaN(dateObj.getTime())) {
        const day = String(dateObj.getDate()).padStart(2, '0');
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const year = dateObj.getFullYear();
        return `${day}/${month}/${year}`;
      }
    }
    
    let day, month, year;
    
    if (dateString.includes('-') && dateString.split('-').length >= 3) {
      const datePart = dateString.split(' ')[0];
      const [dayPart, monthPart, yearPart] = datePart.split('-');
      
      const months = {"Jan": "01", "Feb": "02", "Mar": "03", "Apr": "04", "May": "05", "Jun": "06", 
                     "Jul": "07", "Aug": "08", "Sep": "09", "Oct": "10", "Nov": "11", "Dec": "12"};
      
      day = dayPart;
      month = months[monthPart] || monthPart;
      year = yearPart;
      
      const formattedDay = String(day).padStart(2, '0');
      const formattedMonth = String(month).padStart(2, '0');
      return `${formattedDay}/${formattedMonth}/${year}`;
    }
    
    if (dateString.includes('/') && dateString.split('/').length === 3) {
      const parts = dateString.split('/');
      if (parseInt(parts[0]) > 12) {
        day = parts[0];
        month = parts[1];
        year = parts[2];
      } else if (parseInt(parts[1]) > 12) {
        month = parts[0];
        day = parts[1];
        year = parts[2];
      } else {
        month = parts[0];
        day = parts[1];
        year = parts[2];
      }
      
      const formattedDay = String(day).padStart(2, '0');
      const formattedMonth = String(month).padStart(2, '0');
      return `${formattedDay}/${formattedMonth}/${year}`;
    }
    
    try {
      const dateObj = new Date(dateString);
      if (!isNaN(dateObj.getTime())) {
        const day = String(dateObj.getDate()).padStart(2, '0');
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const year = dateObj.getFullYear();
        return `${day}/${month}/${year}`;
      }
    } catch (e) {
      return dateString;
    }
    
    return dateString;
  };
  
  const handleStartDateChange = (e) => {
    const selectedDate = e.target.value;
    const formattedDate = convertToApiFormat(selectedDate, false);
    setStartDate(formattedDate);
    
    // Set end date constraints based on start date
    if (selectedDate) {
      const startDateObj = new Date(selectedDate);
      const minEndDate = new Date(startDateObj);
      const maxEndDate = new Date(startDateObj);
      maxEndDate.setDate(startDateObj.getDate() + 30);
      
      // Check if maxEndDate is beyond tomorrow
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      
      if (maxEndDate > tomorrow) {
        maxEndDate.setTime(tomorrow.getTime());
      }
      
      // Format dates for input
      const formatDateForInput = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };
      
      setEndDateMin(formatDateForInput(minEndDate));
      setEndDateMax(formatDateForInput(maxEndDate));
    } else {
      setEndDateMin("");
      setEndDateMax("");
    }
  };

  const handleEndDateChange = (e) => {
    const selectedDate = e.target.value;
    const formattedDate = convertToApiFormat(selectedDate, true);
    setEndDate(formattedDate);
  };

  // Function to format profit with color coding
  const formatProfit = (profit) => {
    if (!profit || profit === "0") return "0.00";
    const value = parseFloat(profit);
    return value >= 0 ? `+${value.toFixed(2)}` : value.toFixed(2);
  };

  // Function to get profit color
  const getProfitColor = (profit) => {
    if (!profit || profit === "0") return theme.textTertiary;
    return parseFloat(profit) >= 0 ? theme.success : theme.error;
  };

  // Function to get profit background
  const getProfitBackground = (profit) => {
    if (!profit || profit === "0") return theme.bgTertiary;
    return parseFloat(profit) >= 0 ? `${theme.success}20` : `${theme.error}20`;
  };

  return (
    <div className="p-4 md:p-6 min-h-screen" style={{ background: `linear-gradient(135deg, ${theme.bgPrimary} 0%, ${theme.bgSecondary} 100%)` }}>
      <div className="w-full">
        <div className="mb-6">
          <p className="mt-1" style={{ color: theme.textSecondary }}>View and analyze your trading history</p>
        </div>
        
        <div className="rounded-2xl shadow-xl overflow-hidden" style={{ backgroundColor: theme.bgCard, borderColor: theme.borderPrimary, borderWidth: '1px' }}>
          {/* Header Section */}
          <div className="p-4 md:p-6" style={{ background: theme.brandGradient }}>
            <h2 className="text-xl font-semibold" style={{ color: theme.iconText }}>Filter Trade Orders</h2>
            <p className="mt-1" style={{ color: theme.iconText, opacity: 0.8 }}>Select date range and filters to view your trade history</p>
          </div>
          
          {/* Filter Controls */}
          <div className="p-4 md:p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6">
              <div className="sm:col-span-1">
                <label className="block text-sm font-medium mb-2" style={{ color: theme.textPrimary }}>Start Date</label>
                <div className="relative">
                  <input 
                    type="date" 
                    value={convertToInputFormat(startDate)}
                    onChange={handleStartDateChange}
                    className="w-full px-3 py-2 md:px-4 md:py-3 border rounded-lg shadow-sm focus:outline-none focus:ring-2 transition duration-200 cursor-pointer hover:shadow-md"
                    style={{ 
                      backgroundColor: theme.inputBg, 
                      color: theme.inputText,
                      borderColor: theme.inputBorder,
                    }}
                  />
                </div>
              </div>
              
              <div className="sm:col-span-1">
                <label className="block text-sm font-medium mb-2" style={{ color: theme.textPrimary }}>End Date</label>
                <div className="relative">
                  <input 
                    type="date" 
                    value={convertToInputFormat(endDate)}
                    min={endDateMin || minDate}
                    max={endDateMax || maxDate}
                    onChange={handleEndDateChange}
                    disabled={!startDate}
                    className={`w-full px-3 py-2 md:px-4 md:py-3 border rounded-lg shadow-sm focus:outline-none focus:ring-2 transition duration-200 ${!startDate ? 'cursor-not-allowed' : 'cursor-pointer hover:shadow-md'}`}
                    style={{ 
                      backgroundColor: !startDate ? theme.bgTertiary : theme.inputBg, 
                      color: theme.inputText,
                      borderColor: !startDate ? theme.borderSecondary : theme.inputBorder,
                    }}
                  />
                </div>
                {!startDate && (
                  <p className="mt-1 text-xs" style={{ color: theme.textTertiary }}>Please select a start date first</p>
                )}
              </div>
              
              <div className="sm:col-span-1">
                <label className="block text-sm font-medium mb-2" style={{ color: theme.textPrimary }}>Filter By</label>
                <select 
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  className="w-full px-3 py-2 md:px-4 md:py-3 border rounded-lg shadow-sm focus:outline-none focus:ring-2 transition duration-200 hover:shadow-md"
                  style={{ 
                    backgroundColor: theme.inputBg, 
                    color: theme.inputText,
                    borderColor: theme.inputBorder,
                  }}
                >
                  <option value="recent">Recent Orders</option>
                  <option value="lowToHigh">Quantity: Low to High</option>
                  <option value="highToLow">Quantity: High to Low</option>
                  <option value="maxProfit">Maximum Profit</option>
                  <option value="minProfit">Minimum Profit</option>
                </select>
              </div>
              
              <div className="sm:col-span-1 flex items-end">
                <CommonButton
                  text={loading ? "Loading..." : "Fetch Orders"}
                  onClick={() => handleFetchOrders(1)}
                  disabled={loading || !startDate || !endDate}
                  className={`w-full ${loading || !startDate || !endDate ? 'opacity-70 cursor-not-allowed' : ''}`}
                />
              </div>
            </div>
            
            {error && (
              <div className="mt-4 p-4 md:p-5 border-l-4 rounded-lg shadow-sm" style={{ backgroundColor: `${theme.error}10`, borderColor: theme.error }}>
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5" style={{ color: theme.error }} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium" style={{ color: theme.error }}>Error</h3>
                    <div className="mt-2 text-sm" style={{ color: theme.error, opacity: 0.9 }}>
                      <p>{error}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {loading ? (
              <div className="flex justify-center items-center py-12 md:py-16 rounded-xl border shadow-sm my-4 md:my-6" style={{ backgroundColor: theme.bgCard, borderColor: theme.borderPrimary }}>
                <div className="text-center">
                  <div className="flex justify-center mb-4">
                    <svg className="animate-spin h-10 w-10 md:h-12 md:w-12" style={{ color: theme.brandPrimary }} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </div>
                  <p className="font-medium text-lg" style={{ color: theme.textPrimary }}>Loading trade orders...</p>
                  <p className="text-sm mt-1" style={{ color: theme.textSecondary }}>Please wait while we fetch your data</p>
                </div>
              </div>
            ) : formattedTradeOrders && formattedTradeOrders.length > 0 ? (
              <div className="mt-4 md:mt-6">
                {/* Stats Summary */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4 md:mb-6">
                  <div className="p-3 md:p-4 rounded-xl border" style={{ background: `linear-gradient(135deg, ${theme.brandPrimary}15 0%, ${theme.brandPrimary}05 100%)`, borderColor: theme.brandPrimary }}>
                    <p className="text-sm" style={{ color: theme.textSecondary }}>Total Orders</p>
                    <p className="text-xl md:text-2xl font-bold" style={{ color: theme.textPrimary }}>{summary?.totalOrders || 0}</p>
                  </div>
                  <div className="p-3 md:p-4 rounded-xl border" style={{ background: `linear-gradient(135deg, ${theme.success}15 0%, ${theme.success}05 100%)`, borderColor: theme.success }}>
                    <p className="text-sm" style={{ color: theme.textSecondary }}>Total Profit</p>
                    <p className="text-xl md:text-2xl font-bold" style={{ color: getProfitColor(summary?.totalProfit || 0) }}>
                      {(summary?.totalProfit || 0).toFixed(2)}
                    </p>
                  </div>
                  <div className="p-3 md:p-4 rounded-xl border" style={{ background: `linear-gradient(135deg, ${theme.warning}15 0%, ${theme.warning}05 100%)`, borderColor: theme.warning }}>
                    <p className="text-sm" style={{ color: theme.textSecondary }}>Buy Orders</p>
                    <p className="text-xl md:text-2xl font-bold" style={{ color: theme.warning }}>
                      {summary?.buyOrders || 0}
                    </p>
                  </div>
                  <div className="p-3 md:p-4 rounded-xl border" style={{ background: `linear-gradient(135deg, ${theme.error}15 0%, ${theme.error}05 100%)`, borderColor: theme.error }}>
                    <p className="text-sm" style={{ color: theme.textSecondary }}>Sell Orders</p>
                    <p className="text-xl md:text-2xl font-bold" style={{ color: theme.error }}>
                      {summary?.sellOrders || 0}
                    </p>
                  </div>
                </div>
                
                {/* Orders Table */}
                <div className="overflow-x-auto rounded-xl border shadow-sm" style={{ borderColor: theme.borderPrimary }}>
                  <table className="min-w-full divide-y" style={{ divideColor: theme.borderPrimary }}>
                    <thead style={{ background: theme.brandGradient }}>
                      <tr>
                        <th className="px-3 py-3 md:px-6 md:py-4 text-left text-xs md:text-sm font-medium uppercase tracking-wider" style={{ color: theme.iconText }}>Account</th>
                        <th className="px-3 py-3 md:px-6 md:py-4 text-left text-xs md:text-sm font-medium uppercase tracking-wider" style={{ color: theme.iconText }}>Time</th>
                        <th className="px-3 py-3 md:px-6 md:py-4 text-left text-xs md:text-sm font-medium uppercase tracking-wider" style={{ color: theme.iconText }}>Deal</th>
                        <th className="px-3 py-3 md:px-6 md:py-4 text-left text-xs md:text-sm font-medium uppercase tracking-wider" style={{ color: theme.iconText }}>Symbol</th>
                        <th className="px-3 py-3 md:px-6 md:py-4 text-left text-xs md:text-sm font-medium uppercase tracking-wider" style={{ color: theme.iconText }}>Type</th>
                        <th className="px-3 py-3 md:px-6 md:py-4 text-left text-xs md:text-sm font-medium uppercase tracking-wider" style={{ color: theme.iconText }}>Qty</th>
                        <th className="px-3 py-3 md:px-6 md:py-4 text-left text-xs md:text-sm font-medium uppercase tracking-wider" style={{ color: theme.iconText }}>Price</th>
                        <th className="px-3 py-3 md:px-6 md:py-4 text-left text-xs md:text-sm font-medium uppercase tracking-wider" style={{ color: theme.iconText }}>Brk</th>
                        <th className="px-3 py-3 md:px-6 md:py-4 text-left text-xs md:text-sm font-medium uppercase tracking-wider" style={{ color: theme.iconText }}>Swap</th>
                        <th className="px-3 py-3 md:px-6 md:py-4 text-left text-xs md:text-sm font-medium uppercase tracking-wider" style={{ color: theme.iconText }}>Fee</th>
                        <th className="px-3 py-3 md:px-6 md:py-4 text-left text-xs md:text-sm font-medium uppercase tracking-wider" style={{ color: theme.iconText }}>Profit</th>
                        <th className="px-3 py-3 md:px-6 md:py-4 text-left text-xs md:text-sm font-medium uppercase tracking-wider" style={{ color: theme.iconText }}>Comment</th>
                      </tr>
                    </thead>
                    <tbody style={{ backgroundColor: theme.bgCard }}>
                      {formattedTradeOrders.map((order, index) => (
                        <tr key={index} className="transition duration-150 ease-in-out" style={{ borderBottomColor: theme.borderPrimary, borderBottomWidth: '1px' }}>
                          <td className="px-3 py-3 md:px-6 md:py-4 whitespace-nowrap text-sm font-medium" style={{ color: theme.textPrimary }}>{order.Accountno || "0"}</td>
                          <td className="px-3 py-3 md:px-6 md:py-4 whitespace-nowrap text-xs md:text-sm" style={{ color: theme.textSecondary }}>{order.Time ? formatDateToDDMMYYYY(order.Time) : "0"}</td>
                          <td className="px-3 py-3 md:px-6 md:py-4 whitespace-nowrap text-sm font-medium" style={{ color: theme.textPrimary }}>{order.DealNo || "0"}</td>
                          <td className="px-3 py-3 md:px-6 md:py-4 whitespace-nowrap text-xs md:text-sm" style={{ color: theme.textSecondary }}>{order.Symbol || "0"}</td>
                          <td className="px-3 py-3 md:px-6 md:py-4 whitespace-nowrap text-xs md:text-sm">
                            <span className="px-2 py-1 md:px-3 md:py-1 inline-flex text-xs leading-5 font-semibold rounded-full" style={{ 
                              backgroundColor: order.BS === 'Buy' ? `${theme.success}20` : order.BS === 'Sell' ? `${theme.error}20` : theme.bgTertiary,
                              color: order.BS === 'Buy' ? theme.success : order.BS === 'Sell' ? theme.error : theme.textPrimary
                            }}>
                              {order.BS || "0"}
                            </span>
                          </td>
                          <td className="px-3 py-3 md:px-6 md:py-4 whitespace-nowrap text-xs md:text-sm" style={{ color: theme.textSecondary }}>{order.Qty || "0"}</td>
                          <td className="px-3 py-3 md:px-6 md:py-4 whitespace-nowrap text-xs md:text-sm" style={{ color: theme.textSecondary }}>{order.Price || "0"}</td>
                          <td className="px-3 py-3 md:px-6 md:py-4 whitespace-nowrap text-xs md:text-sm" style={{ color: theme.textSecondary }}>{order.Brk || "0"}</td>
                          <td className="px-3 py-3 md:px-6 md:py-4 whitespace-nowrap text-xs md:text-sm" style={{ color: theme.textSecondary }}>{order.Swap || "0"}</td>
                          <td className="px-3 py-3 md:px-6 md:py-4 whitespace-nowrap text-xs md:text-sm" style={{ color: theme.textSecondary }}>{order.Fee || "0"}</td>
                          <td className="px-3 py-3 md:px-6 md:py-4 whitespace-nowrap text-xs md:text-sm font-medium" style={{ color: getProfitColor(order.Profit) }}>
                            <span className="px-2 py-1 md:px-3 md:py-1 rounded-full text-xs" style={{ backgroundColor: getProfitBackground(order.Profit) }}>
                              {formatProfit(order.Profit)}
                            </span>
                          </td>
                          <td className="px-3 py-3 md:px-6 md:py-4 whitespace-nowrap text-xs md:text-sm max-w-xs truncate" style={{ color: theme.textSecondary }}>{order.Comment || "N/A"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {/* Pagination Controls */}
                <div className="flex flex-col sm:flex-row justify-between items-center mt-4 md:mt-6 gap-4">
                  <div className="text-sm" style={{ color: theme.textPrimary }}>
                    Showing page <span className="font-medium">{pagination?.currentPage || currentPage}</span> of <span className="font-medium">{pagination?.totalPages || 1}</span>
                  </div>
                  <div className="flex space-x-2">
                    <CommonButton
                      text="Previous"
                      onClick={() => handleFetchOrders(currentPage - 1)}
                      disabled={currentPage === 1 || loading}
                      variant="default"
                      className={`${currentPage === 1 || loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                    />
                    <CommonButton
                      text="Next"
                      onClick={() => handleFetchOrders(currentPage + 1)}
                      disabled={currentPage >= (pagination?.totalPages || 1) || loading}
                      variant="default"
                      className={`${currentPage >= (pagination?.totalPages || 1) || loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div className="mt-6 md:mt-8 p-8 md:p-12 rounded-xl border text-center shadow-sm" style={{ background: `linear-gradient(135deg, ${theme.brandPrimary}10 0%, ${theme.brandPrimary}05 100%)`, borderColor: theme.borderPrimary }}>
                <div className="flex justify-center mb-4 md:mb-6">
                  <div className="p-3 md:p-4 rounded-full" style={{ backgroundColor: `${theme.brandPrimary}20` }}>
                    <svg className="h-10 w-10 md:h-12 md:w-12" style={{ color: theme.brandPrimary }} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                  </div>
                </div>
                <h3 className="text-xl md:text-2xl font-bold mb-2" style={{ color: theme.textPrimary }}>No Trade Orders Found</h3>
                <p className="max-w-md mx-auto mb-4 md:mb-6" style={{ color: theme.textSecondary }}>
                  {startDate && endDate 
                    ? "No trade orders found for the selected date range." 
                    : "Please select a date range and click 'Fetch Orders' to view your trade history."}
                </p>
                <div className="flex justify-center gap-3 md:gap-4">
                  <CommonButton
                    text="Reset to Default Dates"
                    onClick={() => {
                      // Reset to default dates (last 14 days)
                      const today = new Date();
                      const last14Days = new Date(today);
                      last14Days.setDate(today.getDate() - 14);
                      
                      const formatDate = (date) => {
                        const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
                        const day = String(date.getDate()).padStart(2, '0');
                        const month = months[date.getMonth()];
                        const year = date.getFullYear();
                        return `${day}-${month}-${year} 00:00:00`;
                      };
                      
                      setStartDate(formatDate(last14Days));
                      setEndDate(formatDate(today));
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};